import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';

import { listGroups } from '@plone/volto/actions/groups/groups';
import { listUsers } from '@plone/volto/actions/users/users';
import type { MpfPrincipal } from 'volto-ministerio-publico-federal/helpers/Permissoes';

const messages = defineMessages({
  placeholder: {
    id: 'Search users or groups',
    defaultMessage: 'Buscar usuários ou grupos (mínimo 2 letras)',
  },
  noResults: {
    id: 'No users or groups found',
    defaultMessage: 'Nenhum usuário ou grupo encontrado',
  },
  group: {
    id: 'Access profile group label',
    defaultMessage: '{title} (grupo)',
  },
});

// O backend recusa perfil nestes grupos (ver perfis/manager.py).
const GRUPOS_PROIBIDOS = [
  'AuthenticatedUsers',
  'Administrators',
  'Site Administrators',
];

const BUSCA_MINIMA = 2;

type Props = {
  onSelecionar: (principal: MpfPrincipal) => void;
  ignorar: string[];
};

const PrincipaisPicker = ({ onSelecionar, ignorar }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [busca, setBusca] = useState('');
  const users = useSelector((state: any) => state.users?.users ?? []);
  const groups = useSelector((state: any) => state.groups?.groups ?? []);
  const buscaValida = busca.trim().length >= BUSCA_MINIMA;

  useEffect(() => {
    if (!buscaValida) return undefined;
    const timer = setTimeout(() => {
      dispatch(listUsers({ query: busca.trim() }));
      dispatch(listGroups(busca.trim()));
    }, 300);
    return () => clearTimeout(timer);
  }, [busca, buscaValida, dispatch]);

  const opcoes = useMemo(() => {
    if (!buscaValida) return [];
    const grupos = groups
      .filter((grupo: any) => !GRUPOS_PROIBIDOS.includes(grupo.id))
      .map((grupo: any) => ({
        key: `group:${grupo.id}`,
        value: `group:${grupo.id}`,
        text: intl.formatMessage(messages.group, {
          title: grupo.title || grupo.id,
        }),
      }));
    const usuarios = users.map((usuario: any) => ({
      key: `user:${usuario.id}`,
      value: `user:${usuario.id}`,
      text: usuario.fullname
        ? `${usuario.fullname} (${usuario.id})`
        : usuario.id,
    }));
    return [...grupos, ...usuarios].filter(
      (opcao) =>
        !ignorar.includes(opcao.value.slice(opcao.value.indexOf(':') + 1)),
    );
  }, [buscaValida, groups, users, ignorar, intl]);

  return (
    <Dropdown
      fluid
      search
      selection
      selectOnBlur={false}
      value=""
      options={opcoes}
      searchQuery={busca}
      placeholder={intl.formatMessage(messages.placeholder)}
      noResultsMessage={
        buscaValida ? intl.formatMessage(messages.noResults) : null
      }
      onSearchChange={(_event, { searchQuery }) => setBusca(searchQuery)}
      onChange={(_event, { value }) => {
        const valor = String(value);
        const separador = valor.indexOf(':');
        const opcao = opcoes.find((item) => item.value === valor);
        onSelecionar({
          id: valor.slice(separador + 1),
          type: valor.slice(0, separador) as MpfPrincipal['type'],
          title: opcao?.text ?? valor,
        });
        setBusca('');
      }}
    />
  );
};

export default PrincipaisPicker;
